import {
  CardMedia,
  CircularProgress,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import send from '@images/send.svg';
import paperClip from '@images/paperClip.png';
import { MentionsInput, Mention } from 'react-mentions';
import mentionStyle from './mentionStyles';
import mentionsInputStyle from './mentionsInputStyles';
import MyAvatar from '../admin/MyAvatar';
import './CommentInput.css';
// removed unused useSelector
import { toast } from 'react-toastify';
import axios from '@MyAxios';
import EmojiSelector from '@components/emoji-selector/EmojiSelector';
import OpenGraphInfo from '@components/OpenGraphInfo';
import validator from 'validator';

const useStyles = makeStyles((theme) => ({
  textColor: { color: theme.palette.primary.main },
  button: { backgroundColor: theme.palette.primary.main, color: '#fff' },
  textGrey: { color: '#626262' },
  colorGreen: { backgroundColor: '#e5f8f1' },
  bgGreen: { backgroundColor: theme.palette.primary.main, color: '#fff' },
}));

// ── URL extractor ─────────────────────────────────────────────────────────────
const extractUrl = (text) => {
  if (!text) return null;
  let t = String(text).trim();
  try {
    t = decodeURIComponent(t);
  } catch (e) {}
  t = t.replace(/^<|>$/g, '');

  const httpRegex = /https?:\/\/[^\s<>'")]+/i;
  const wwwRegex = /www\.[^\s<>'")]+/i;
  const domainRegex =
    /(?:https?:\/\/)?(?:www\.)?[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/[^\s<>'"]*)?/i;

  let match = t.match(httpRegex) || t.match(wwwRegex) || t.match(domainRegex);
  if (!match) return null;

  let link = match[0].replace(/[.,;:!?')\]]+$/g, '');
  if (!/^https?:\/\//i.test(link)) link = 'https://' + link;

  try {
    return new URL(link).toString();
  } catch (e) {
    return null;
  }
};

export default function CommentInput({
  onSubmit,
  value,
  setValue,
  setFile,
  prevFile,
  setPrevFile,
  loading,
  // OG props — managed by the parent (ViewEvent / ViewPost)
  ogData,
  ogLoading,
  onDismissOg,
  onOgFetched,
}) {
  const classes = useStyles();
  const fileInput = useRef(null);

  const [prevFileType, setPrevFileType] = useState();
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionSuggestionsLoading, setMentionSuggestionsLoading] =
    useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const [hideLinkPreview, setHideLinkPreview] = useState(false);

  // lastFetchedRef: dedup guard — don't re-fetch the exact same URL twice in a row
  // (no firstLinkRef so URLs anywhere in the text are all eligible)
  const lastFetchedRef = useRef(null);

  // Reset when comment is cleared after submit
  useEffect(() => {
    if (!value) {
      lastFetchedRef.current = null;
      setHideLinkPreview(false);
    }
  }, [value]);

  // ── Mentions detection ────────────────────────────────────────────────────
  useEffect(() => {
    if (!value) return;
    let mentionStrings = value.match(/\B@[a-z0-9_-]+/gi);
    setMentionSuggestions([]);
    if (mentionStrings?.length) {
      mentionStrings = mentionStrings.map((str) => str.slice(1));
      setMentionSuggestionsLoading(true);
      axios
        .get('/user/get-user-by-usernames', {
          params: { usernames: mentionStrings },
        })
        .then((response) => setMentionSuggestions(response.data))
        .finally(() => setMentionSuggestionsLoading(false));
    }
  }, [value]);

  // ── OG fetch — called ONLY on Space / Enter / blur ────────────────────────
  // Uses e.target.value directly to avoid stale closure on React state.
  // No firstLinkRef: any URL in the text (beginning, middle, end) triggers OG
  // as long as it's different from the last fetched one.
  const tryFetchOG = (currentValue) => {
    if (hideLinkPreview || !onOgFetched) return;

    const extracted = extractUrl(currentValue || '');
    if (!extracted) return;

    // Same URL as last fetch — skip (dedup)
    if (extracted === lastFetchedRef.current) return;

    if (!validator.isURL(extracted)) return;

    lastFetchedRef.current = extracted;
    onOgFetched(extracted);
  };

  const onFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      if (e.target.files[0].size > 3 * 1024 * 1024) {
        toast.error('File size should be less than 3MB');
        return;
      }
      setFile(e.target.files[0]);
      setPrevFile(URL.createObjectURL(e.target.files[0]));
      setPrevFileType(e.target.files[0].type.split('/')[0]);
    }
    if (e.target.value) e.target.value = null;
  };

  const getRepliedToUser = (mention) => {
    const endIndex = mention.indexOf(']', 2);
    return mention.slice(2, endIndex);
  };

  const getRenderedValue = () => {
    const matchRegex = /@\[([a-zA-Z0-9]+)\]\(([a-zA-Z0-9]+)\)/gi;
    return {
      renderedText: value?.replace(matchRegex, getRepliedToUser),
      replacers:
        value
          ?.match(matchRegex)
          ?.reduce(
            (acc, curr) => ({ ...acc, [getRepliedToUser(curr)]: curr }),
            {},
          ) ?? {},
    };
  };

  const handleSelectEmoji = (emoji) => {
    const { renderedText, replacers } = getRenderedValue();
    const valueArray = (renderedText ?? '').split('');
    valueArray.splice(cursorPos, 0, emoji);
    let newValue = valueArray.join('');
    Object.keys(replacers).forEach((username) => {
      newValue = newValue.replace(
        new RegExp(username, 'gi'),
        replacers[username],
      );
    });
    setValue(newValue);
  };

  const handleDismiss = () => {
    setHideLinkPreview(true);
    onDismissOg?.();
  };

  const handleShowPreview = () => {
    setHideLinkPreview(false);
    // Re-fetch using the last known URL
    if (lastFetchedRef.current) {
      const prev = lastFetchedRef.current;
      lastFetchedRef.current = null; // clear dedup so re-fetch fires
      onOgFetched?.(prev);
      lastFetchedRef.current = prev;
    }
  };

  return (
    <div className="fixed bottom-0 flex flex-col w-full items-center justify-center md:w-6/12 bg-white">
      {/* ── File preview ── */}
      {prevFile && (
        <div
          className={clsx(
            `my-2 flex flex-col relative rounded-xl p-6`,
            classes.bgGreen,
          )}
        >
          <Typography
            color={'red'}
            onClick={() => {
              setFile('');
              setPrevFile('');
              setPrevFileType('');
            }}
            className="cursor-pointer absolute top-3 left-3"
          >
            X
          </Typography>
          <div>
            {prevFileType === 'video' ? (
              <CardMedia
                controlsList="nodownload"
                crossOrigin="anonymous"
                component={'video'}
                controls
                className="w-52 h-40 object-contain"
                image={prevFile}
                alt="Stream Media"
                autoPlay
                loop
                muted
              />
            ) : (
              <img
                src={prevFile}
                className="w-52 h-40 object-contain"
                alt="Camera Chat"
              />
            )}
          </div>
        </div>
      )}

      {/* ── OG link preview — same UI as CreateEvent / CreatePost ── */}
      {hideLinkPreview ? (
        <div className="flex justify-end my-2">
          <Button variant="contained" onClick={handleShowPreview}>
            Show link preview
          </Button>
        </div>
      ) : ogLoading ? (
        <div className="flex items-center justify-center py-6">
          <CircularProgress size={24} />
          {/* <span>Fetching link preview…</span> */}
        </div>
      ) : ogData ? (
        <div className="relative my-2">
          <OpenGraphInfo data={ogData} />
          <Tooltip title="Remove link preview">
            <CloseIcon
              fontSize="small"
              className="cursor-pointer absolute top-0 right-3 text-gray-500"
              onClick={handleDismiss}
            />
          </Tooltip>
        </div>
      ) : null}

      {/* ── Input row ── */}
      <form onSubmit={onSubmit} className="flex items-center p-4 relative">
        <input
          className="hidden"
          multiple={true}
          type="file"
          accept="image/*,video/*"
          ref={fileInput}
          onChange={onFileChange}
        />
        <img
          src={paperClip}
          alt="Attach"
          className="h-5 w-5 cursor-pointer"
          onClick={() => fileInput.current.click()}
        />
        <MentionsInput
          value={value}
          style={mentionsInputStyle}
          className={clsx('mx-2 w-72 noto-font')}
          placeholder="Comment"
          allowSuggestionsAboveCursor
          onSelectCapture={(e) => setCursorPos(e.target.selectionStart)}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') tryFetchOG(e.target.value);
          }}
          onBlur={(e) => tryFetchOG(e.target.value)}
        >
          <Mention
            trigger="@"
            appendSpaceOnAdd
            style={mentionStyle}
            key={mentionSuggestionsLoading}
            isLoading={mentionSuggestionsLoading}
            data={mentionSuggestions?.map((user) => ({
              id: user.userName,
              display: user.userName,
            }))}
            renderSuggestion={(suggestion, search, value, index, focused) => (
              <MyAvatar
                name={value}
                src={
                  mentionSuggestions?.find(
                    (user) => user.userName === suggestion.id,
                  )?.media?.currBucketUrl
                }
              />
            )}
          />
        </MentionsInput>
        <EmojiSelector
          className="-ml-12 mr-3"
          onSelection={handleSelectEmoji}
        />
        {loading ? (
          <CircularProgress />
        ) : (
          <button className="w-16" type="submit">
            <img src={send} alt="Send" />
          </button>
        )}
      </form>
    </div>
  );
}
